import { BsPersonPlusFill } from "react-icons/all";
import { useColorModeValue, VStack } from "@chakra-ui/react";

import {
  CheckBoxField,
  Form,
  RecaptchaField,
  SpinnerButton,
  TextField,
  TextFieldType,
  IErrorField,
} from "components/Form";
import { Icon } from "components/Icon";
import { ChakraText } from "components/ChakraText";
import { useGetThemeColors } from "ChakraTheme";
import { MaskedTextField } from "components/Form/MaskedTextField";
import { DangerousInnerHtml } from "components/DangerousInnerHtml";

import { useRegisterFormValidationSchema } from "../application/validation/useRegisterFormValidationSchema";
import { IRegisterForm } from "../application/types/IRegisterForm";
import { useRegisterUser } from "../infrastructure/useRegisterUser";
import { PasswordStrengthBar } from "./PasswordStrengthBar";
import { useRegisterNotification } from "./useRegisterNotification";
import { useContextConsumer } from "../../context/Providers/ContextProvider";

interface IProps {
  onCloseModal: () => void;
  appointmentId?: string;
}

const RegisterForm = ({ onCloseModal, appointmentId }: IProps) => {
  const colors = useGetThemeColors();
  const iconColor = useColorModeValue(colors.white, colors.black);
  const {
    showSuccessNotification,
    showErrorNotification,
  } = useRegisterNotification();
  const { personalDataAgreement, regulationsAgreement } = useContextConsumer();

  const mutation = useRegisterUser();

  const handleSubmit = async (
    model: IRegisterForm,
    setCustomErrors: (errors: IErrorField[]) => void
  ) => {
    const formData = new FormData();
    Object.keys(model).forEach((key) => {
      formData.append(
        key,
        model[key as keyof typeof model]
          ? model[key as keyof typeof model].toString()
          : ""
      );
    });
    appointmentId && formData.append("appointmentId", appointmentId);
    try {
      await mutation.mutateAsync(formData);
      showSuccessNotification();
      onCloseModal();
    } catch (e) {
      if (e.status === 400) {
        setCustomErrors(e.response.msg);
        showErrorNotification();
        return;
      }
      showErrorNotification(e.message);
    }
  };

  return (
    <Form<IRegisterForm>
      resetOnSubmit={false}
      onSubmit={handleSubmit}
      defaultValues={{
        name: "",
        email: "",
        phone: "",
        password: "",
        repeatPassword: "",
        "recaptcha-response": "",
        termAccept: 0,
      }}
      validationSchema={useRegisterFormValidationSchema()}
    >
      {({ watch }) => {
        const password = watch("password");
        return (
          <VStack spacing={4} align="stretch">
            <TextField label="Imię i nazwisko" name="name" />
            <TextField label="Adres email" name="email" />
            <MaskedTextField
              mask={[/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
              label="Numer telefonu"
              name="phone"
              isRequired={false}
            />
            <TextField
              label="Hasło"
              name="password"
              type={TextFieldType.PASSWORD}
            />
            <TextField
              label="Powtórz hasło"
              name="repeatPassword"
              type={TextFieldType.PASSWORD}
            />

            <PasswordStrengthBar password={password} />

            <RecaptchaField mt={4} name="recaptcha-response" />
            <DangerousInnerHtml html={regulationsAgreement} />
            <CheckBoxField
              mt={2}
              name="termAccept"
              label={<DangerousInnerHtml html={personalDataAgreement} />}
            />

            <VStack mt={8}>
              <SpinnerButton
                btnTitle={<ChakraText>Zarejestruj się</ChakraText>}
                leftIcon={<Icon color={iconColor} icon={BsPersonPlusFill} />}
                w="250px"
                id="register-btn"
              />
            </VStack>
          </VStack>
        );
      }}
    </Form>
  );
};

export { RegisterForm };
